import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import moment from 'moment';
import { environment } from 'src/environments/environment';
import { Warehouse } from '../models/warehouse.model';
import { WarehouseLocation } from '../models/warehouse-location.model';
import { ProductStockLevel } from '../models/product-stock-level.model';
import { ProductStockMovement, MOVEMENT_TYPE_LABELS } from '../models/product-stock-movement.model';
import {
  WarehousingDashboard,
  WarehouseOccupancy,
  StockException,
  RecentMovement
} from '../models/warehousing-dashboard.model';
import { WarehouseService } from './warehouse.service';
import { WarehouseLocationService } from './warehouse-location.service';
import { StockQuantityService } from './stock-quantity.service';
import { WarehousingChartService } from './warehousing-chart.service';
import { WarehouseReportingService } from './warehouse-reporting.service';
import { DashboardSummary } from '../models/warehouse-reporting.model';

/** How many rows the recent-movements feed shows. */
const RECENT_MOVEMENT_LIMIT: number = 20;

/**
 * How much ledger the movement charts cover.
 *
 * Bounded by time rather than by row count, and never unbounded: the ledger is append-only
 * and grows forever, so "all movements" is not a query this screen may make. A trend chart
 * wants a recent window anyway, so the bound is the honest shape of the question.
 */
const CHART_WINDOW_DAYS: number = 30;

@Injectable({
  providedIn: 'root'
})
export class WarehousingDashboardService {

  /**
  * Endpoint for http calls
  */
  private _endpoint: string = environment.api.endpoint;

  constructor(private httpClient: HttpClient,
    private warehouseService: WarehouseService,
    private warehouseLocationService: WarehouseLocationService,
    private stockQuantityService: StockQuantityService,
    private warehousingChartService: WarehousingChartService,
    private warehouseReportingService: WarehouseReportingService) { }

  /**
   * Everything the dashboard shows, in one subscription.
   *
   * The figures are derived here from the read-only CRUD routes rather than fetched from
   * a server-side aggregate, because product_stock_level is bounded by products x locations
   * rather than by the ledger, which grows forever. The movement count is the one figure
   * that would need the whole ledger, so it is asked for as a paginated total instead.
   *
   * KNOWN LIMITATION — the exception *lists* are still derived here, and cannot match the
   * backend's classification. `summary` now carries the authoritative counts, so where the
   * two disagree the summary is right. Still to do: move the lists to stock-exceptions/.
   *
   *   Deciding zero and negative here cannot match the backend. The backend tolerance is
   *   `QTY_EPSILON + |largest single qty in that location's ledger| * 2^-23`, scaled by the
   *   location's throughput. Rounding to 4dp gives this service an effective flat 0.00005,
   *   which matches the constant term but has no scaled term, so on a location holding
   *   ~1200 units a residual of ~1.4e-4 reads as stock here and as zero there.
   *
   *   That can raise a false negative-stock alarm and can count an empty shelf as holding
   *   stock. It is defect 3 of the status report surfacing in the UI, and it is NOT fixable
   *   here: the scaled term needs the location's largest movement, which would mean
   *   downloading the ledger. Measured live the backend tolerance is ~6.05e-05 against this
   *   service's flat 5e-05, so the backend's `isNegative` / `matchesLedger` flags are the
   *   source of truth, not this code.
   */
  getDashboard(): Observable<WarehousingDashboard> {
    return forkJoin({
      levels: this.getAllStockLevels(),
      locations: this.warehouseLocationService.getAllWarehouseLocations(),
      warehouses: this.warehouseService.getAllWarehouses(),
      recentMovements: this.getRecentMovements(),
      movementsToday: this.getMovementCountSince(moment().startOf('day')),
      chartMovements: this.getMovementsSince(moment().startOf('day').subtract(CHART_WINDOW_DAYS, 'days')),
      // Valuation and the throughput-scaled zero/negative classification can only come from
      // the server. Failure here must not take the whole dashboard down, so it degrades to
      // null and the money tile says it could not be loaded rather than showing a zero.
      summary: this.warehouseReportingService.getDashboardSummary()
        .pipe(catchError(() => of(null)))
    }).pipe(map((result: any) => this.assemble(result)));
  }

  /**
   * Movements within a window, for the charts. Date-bounded and joined only as far as the
   * charts need — they group by movement type and warehouse, not by product detail.
   *
   * @param since inclusive lower bound on createdAt
   */
  getMovementsSince(since: moment.Moment): Observable<ProductStockMovement[]> {
    const from: string = since.format('YYYY-MM-DD HH:mm:ss');

    return this.httpClient.get(this._endpoint + 'product-stock-movement'
      + '?join=warehouseLocation&join=warehouseLocation.warehouse'
      + '&filter=createdAt||gte||' + encodeURIComponent(from)
      + '&sort=id,DESC').pipe(map((data) => {
        return <ProductStockMovement[]>data;
      }));
  }

  /**
   * Every stock level with the joins the dashboard and the stock-on-hand report need.
   *
   * No limit is set, so nestjs-crud answers with a plain array rather than a page. The
   * dashboard has to total *all* stock, so a page would silently under-report.
   */
  getAllStockLevels(): Observable<ProductStockLevel[]> {
    return this.httpClient.get(this._endpoint + 'product-stock-level'
      + '?join=product&join=warehouseLocation&join=warehouseLocation.warehouse'
      + '&sort=id,DESC').pipe(map((data) => {
        return <ProductStockLevel[]>data;
      }));
  }

  getRecentMovements(): Observable<ProductStockMovement[]> {
    return this.httpClient.get(this._endpoint + 'product-stock-movement'
      + '?join=product&join=warehouseLocation&join=warehouseLocation.warehouse&join=reference'
      + '&sort=id,DESC&limit=' + RECENT_MOVEMENT_LIMIT + '&page=1').pipe(map((data: any) => {
        // A limit makes this route paginated, so the rows arrive under `data`.
        return <ProductStockMovement[]>(data && data.data ? data.data : data);
      }));
  }

  /**
   * Counts movements without downloading them: ask for a single row and read the total
   * off the pagination envelope.
   *
   * @param since inclusive lower bound on createdAt
   */
  getMovementCountSince(since: moment.Moment): Observable<number> {
    // createdAt is a MySQL datetime written in server local time, so the bound is
    // formatted as local wall-clock rather than as an ISO instant.
    const from: string = since.format('YYYY-MM-DD HH:mm:ss');

    return this.httpClient.get(this._endpoint + 'product-stock-movement'
      + '?filter=createdAt||gte||' + encodeURIComponent(from)
      + '&limit=1&page=1').pipe(map((data: any) => {
        return data && data.total ? +data.total : 0;
      }));
  }

  private assemble(result: any): WarehousingDashboard {
    const dashboard: WarehousingDashboard = new WarehousingDashboard();

    const levels: ProductStockLevel[] = result.levels ? result.levels : [];
    const locations: WarehouseLocation[] = result.locations ? result.locations : [];
    const warehouses: Warehouse[] = result.warehouses ? result.warehouses : [];

    dashboard.kpis.movementsToday = result.movementsToday;
    dashboard.kpis.totalStockQty = this.stockQuantityService.round(
      levels.reduce((total: number, level: ProductStockLevel) => total + Number(level.qty), 0));

    // A level sitting at zero holds no stock, and qty is a float, so "holding stock" is
    // tested through the shared epsilon and never against zero exactly.
    const holding: ProductStockLevel[] = levels.filter(l => !this.stockQuantityService.isZero(l.qty));

    dashboard.kpis.productsHoldingStock = this.countDistinct(holding.map(l => l.productId));
    dashboard.kpis.locationsInUse = this.countDistinct(holding.map(l => l.warehouseLocationId));

    dashboard.occupancy = this.buildOccupancy(warehouses, locations, holding, levels);

    // Negative stock is a defect, not an empty shelf: the level is SUM over an append-only
    // ledger, so a negative total means movements are missing or mis-signed.
    dashboard.negative = levels
      .filter(l => this.stockQuantityService.isNegative(l.qty))
      .map(l => this.toException(l));

    dashboard.zero = levels
      .filter(l => this.stockQuantityService.isZero(l.qty))
      .map(l => this.toException(l));

    dashboard.recentMovements = (result.recentMovements ? result.recentMovements : [])
      .map((m: ProductStockMovement) => this.toRecentMovement(m));

    const chartMovements: ProductStockMovement[] = result.chartMovements ? result.chartMovements : [];

    dashboard.movementWindowDays = CHART_WINDOW_DAYS;
    dashboard.stockShareByWarehouse = this.warehousingChartService.stockShareByWarehouse(levels, warehouses);
    dashboard.topProductsByQty = this.warehousingChartService.topProductsByQty(levels);
    dashboard.movementsByType = this.warehousingChartService.movementsByType(chartMovements, warehouses);
    dashboard.movementsByDay = this.warehousingChartService.movementsByDay(chartMovements);

    if (result.summary) {
      dashboard.summary = <DashboardSummary>result.summary;
      dashboard.summaryLoaded = true;
    }

    return dashboard;
  }

  private buildOccupancy(warehouses: Warehouse[],
    locations: WarehouseLocation[],
    holding: ProductStockLevel[],
    levels: ProductStockLevel[]): WarehouseOccupancy[] {

    return warehouses.map((warehouse: Warehouse) => {
      const row: WarehouseOccupancy = new WarehouseOccupancy();

      row.warehouseId = warehouse.id;
      row.warehouseName = warehouse.name;
      row.locationCount = locations.filter(l => l.warehouseId === warehouse.id).length;

      const atSite: ProductStockLevel[] = levels.filter(l => this.warehouseIdOf(l) === warehouse.id);
      const holdingAtSite: ProductStockLevel[] = holding.filter(l => this.warehouseIdOf(l) === warehouse.id);

      row.locationsInUse = this.countDistinct(holdingAtSite.map(l => l.warehouseLocationId));
      row.productCount = this.countDistinct(holdingAtSite.map(l => l.productId));
      row.totalQty = this.stockQuantityService.round(
        atSite.reduce((total: number, level: ProductStockLevel) => total + Number(level.qty), 0));

      // More locations in use than exist means stock is sitting at a warehouse_location row
      // that is no longer there. That is reachable: there are zero foreign keys in this
      // schema, so nothing at the database level stops a level outliving its location.
      // Rendering "3 / 0" and a 300% bar would look like a display bug rather than the
      // integrity problem it is, so it is called out instead.
      row.orphanedLocations = Math.max(0, row.locationsInUse - row.locationCount);

      row.usedPercent = row.locationCount > 0
        ? Math.min(100, Math.round((row.locationsInUse / row.locationCount) * 100))
        : 0;

      return row;
    });
  }

  /**
   * A level points at a location, and the location carries the site. The join can come
   * back absent, so this never assumes the nested object is there.
   */
  private warehouseIdOf(level: ProductStockLevel): number {
    if (level.warehouseLocation && level.warehouseLocation.warehouseId) {
      return level.warehouseLocation.warehouseId;
    }

    return -1;
  }

  private toException(level: ProductStockLevel): StockException {
    const exception: StockException = new StockException();

    exception.productStockLevelId = level.id;
    exception.productId = level.productId;
    exception.productName = level.product ? level.product.name : '';
    exception.sku = level.product ? level.product.sku : '';
    exception.warehouseLocationId = level.warehouseLocationId;
    exception.qty = this.stockQuantityService.round(level.qty);

    if (level.warehouseLocation) {
      exception.aisle = level.warehouseLocation.aisle;
      exception.shelf = level.warehouseLocation.shelf;
      exception.warehouseId = level.warehouseLocation.warehouseId;
      exception.warehouseName = level.warehouseLocation.warehouse
        ? level.warehouseLocation.warehouse.name : '';
    }

    return exception;
  }

  private toRecentMovement(movement: ProductStockMovement): RecentMovement {
    const recent: RecentMovement = new RecentMovement();

    recent.id = movement.id;
    recent.createdAt = movement.createdAt;
    recent.productId = movement.productId;
    recent.productName = movement.product ? movement.product.name : '';
    recent.sku = movement.product ? movement.product.sku : '';
    recent.movementType = movement.movementType;
    recent.movementTypeLabel = this.movementTypeLabel(movement.movementType);
    recent.qty = this.stockQuantityService.round(movement.qty);

    // Copied across as they stand, and NOT put through the quantity rounding above. That rounding
    // answers 0 for anything it cannot use, which would turn an unweighed movement into one that
    // weighed nothing; a weight is exact in the database and needs no rounding to begin with.
    recent.startWeight = movement.startWeight;
    recent.endWeight = movement.endWeight;

    if (movement.warehouseLocation) {
      recent.aisle = movement.warehouseLocation.aisle;
      recent.shelf = movement.warehouseLocation.shelf;
      recent.warehouseName = movement.warehouseLocation.warehouse
        ? movement.warehouseLocation.warehouse.name : '';
    }

    if (movement.reference) {
      recent.referenceName = movement.reference.name;
      recent.referenceCode = movement.reference.code;
    }

    return recent;
  }

  private movementTypeLabel(movementType: string): string {
    const type: any = MOVEMENT_TYPE_LABELS.filter(t => t.value === movementType)[0];

    return type ? type.label : movementType;
  }

  private countDistinct(values: number[]): number {
    const seen: any = {};
    let count: number = 0;

    values.forEach((value: number) => {
      if (!seen[value]) {
        seen[value] = true;
        count++;
      }
    });

    return count;
  }
}
